import { useEffect, useMemo, useState } from 'react';
import { Input, Modal, List, Tag, Empty, Typography } from 'antd';
import { Search, FileText, Database, BarChart3 } from 'lucide-react';
import type { SubsystemDto } from '@api/subsystemsApi';
import type { MetaDocumentTypeDto } from '@/types/metadata';

interface CommandPaletteProps {
  acqOpen: boolean;
  acqOnClose: () => void;
  acqSubsystems: SubsystemDto[];
  acqDocTypes: MetaDocumentTypeDto[];
  acqResolveModuleKey: (acqItemType: number, acqItemRef: string) => string | null;
  acqOnSelect: (acqModuleKey: string) => void;
}

interface CommandEntry {
  key: string;       // унікальний для рендера
  type: 'doc' | 'subsys' | 'item';
  label: string;
  hint?: string;
  moduleKey: string; // куди йти
  icon: JSX.Element;
}

/**
 * Глобальний пошук (Ctrl+K). Шукає по:
 *   • назвах meta_document_types
 *   • назвах підсистем
 *   • items усіх підсистем (через резолвер)
 * Topi 30 entry. Підсвітка набраного — простий substring match (case-insensitive).
 */
export default function CommandPalette({
  acqOpen,
  acqOnClose,
  acqSubsystems,
  acqDocTypes,
  acqResolveModuleKey,
  acqOnSelect,
}: CommandPaletteProps) {
  const [acqQ, setAcqQ] = useState('');

  useEffect(() => {
    if (!acqOpen) setAcqQ('');
  }, [acqOpen]);

  const acqEntries: CommandEntry[] = useMemo(() => {
    const acqOut: CommandEntry[] = [];

    // 1. Документи (meta_document_types)
    for (const acqT of acqDocTypes) {
      acqOut.push({
        key: `doc-${acqT.code}`,
        type: 'doc',
        label: acqT.name,
        hint: 'Документ',
        moduleKey: `doc-type-${acqT.code}`,
        icon: <FileText size={14} />,
      });
    }

    // 2. Підсистеми та їх items
    const acqWalk = (acqList: SubsystemDto[]) => {
      for (const acqS of acqList) {
        acqOut.push({
          key: `subsys-${acqS.id}`,
          type: 'subsys',
          label: acqS.name,
          hint: 'Підсистема',
          moduleKey: `subsys-tab-${acqS.code}`, // спеціальний ключ — відкривається MegaMenu
          icon: <Database size={14} />,
        });
        for (const acqSec of acqS.sections ?? []) {
          for (const acqIt of acqSec.items) {
            const acqResolved = acqResolveModuleKey(acqIt.itemType, acqIt.itemRef);
            if (!acqResolved) continue;
            acqOut.push({
              key: `item-${acqIt.id}`,
              type: 'item',
              label: acqIt.displayName ?? acqIt.itemRef,
              hint: acqS.name,
              moduleKey: acqResolved,
              icon: acqIt.itemType === 2 ? <FileText size={14} /> : acqIt.itemType === 3 ? <BarChart3 size={14} /> : <Database size={14} />,
            });
          }
        }
        if (acqS.children?.length) acqWalk(acqS.children);
      }
    };
    acqWalk(acqSubsystems);
    return acqOut;
  }, [acqSubsystems, acqDocTypes, acqResolveModuleKey]);

  const acqFiltered = useMemo(() => {
    const acqQuery = acqQ.trim().toLowerCase();
    if (!acqQuery) return acqEntries.slice(0, 30);
    return acqEntries
      .filter((e) => e.label.toLowerCase().includes(acqQuery))
      .slice(0, 30);
  }, [acqEntries, acqQ]);

  const acqHandleSelect = (acqEntry: CommandEntry) => {
    acqOnSelect(acqEntry.moduleKey);
    acqOnClose();
  };

  return (
    <Modal
      open={acqOpen}
      onCancel={acqOnClose}
      footer={null}
      width={600}
      closable={false}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
    >
      <Input
        autoFocus
        size="large"
        prefix={<Search size={16} />}
        placeholder="Пошук документа, контрагента, звіту…"
        value={acqQ}
        onChange={(e) => setAcqQ(e.target.value)}
        onPressEnter={() => acqFiltered[0] && acqHandleSelect(acqFiltered[0])}
        style={{ border: 'none', borderBottom: '1px solid #f0f0f0', borderRadius: '8px 8px 0 0' }}
      />
      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {acqFiltered.length === 0 ? (
          <div style={{ padding: 32 }}>
            <Empty description={acqQ ? `Нічого не знайдено за «${acqQ}»` : 'Почніть друкувати'} />
          </div>
        ) : (
          <List
            size="small"
            dataSource={acqFiltered}
            renderItem={(acqE) => (
              <List.Item
                onClick={() => acqHandleSelect(acqE)}
                style={{ cursor: 'pointer', padding: '8px 16px' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ marginRight: 10, color: '#8c8c8c' }}>{acqE.icon}</span>
                <Typography.Text style={{ flex: 1, fontSize: 13 }}>{acqE.label}</Typography.Text>
                {acqE.hint && <Tag style={{ fontSize: 10 }}>{acqE.hint}</Tag>}
              </List.Item>
            )}
          />
        )}
      </div>
      <div style={{
        padding: '6px 16px',
        borderTop: '1px solid #f0f0f0',
        fontSize: 11,
        color: '#8c8c8c',
        display: 'flex',
        gap: 12,
      }}>
        <span>Enter — відкрити</span>
        <span>Esc — закрити</span>
        <span style={{ marginLeft: 'auto' }}>{acqFiltered.length} з {acqEntries.length}</span>
      </div>
    </Modal>
  );
}
